import { Button, Input, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import Popup from 'components/molecules/popup';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import i18next from 'i18next';
import middleware from 'middleware';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiGlobeSimple, PiPlus, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  usePostAllDigitalDeliveries,
  usePutAllDigitalDeliveries,
} from 'services/digital-delivery';
import {
  DELIVERY_METHOD,
  ICreateAllDigitalDeliveries,
  IDigitalDeliveryBase,
} from 'services/digital-delivery/types';
import { useGetCountries } from 'services/enums';
import { useGetMusicProductDigitalDeliveryConsistent } from 'services/music-product-digital/delivery';
import {
  DELIVERY_STATUS,
  SUBSCRIPTION_TYPE,
} from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDateToInput } from 'utils/global/date.utils';
import { requiredEnumSelect } from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { array, boolean, object, string } from 'yup';

const digitalDeliveryInitialValues = (
  publishDate?: string
): IDigitalDeliveryBase => ({
  asap: false,
  isTimeZoneBased: false,
  countryCode: '',
  publishDate: publishDate ?? formatDateToInput(new Date()),
  publishTime: '00:00',
  subscriptionTypeId: SUBSCRIPTION_TYPE.all,
  endDate: null,
  endTime: null,
});

const digitalDeliveryBaseValidator = object().shape({
  asap: boolean().required(i18next.t('formik-error.required')),
  isTimeZoneBased: boolean().required(i18next.t('formik-error.required')),
  countryCode: string().required(i18next.t('formik-error.required')),
  publishDate: string().when('asap', {
    is: (asap: boolean) => !asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  publishTime: string().when('asap', {
    is: (asap: boolean) => !asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  subscriptionTypeId: requiredEnumSelect(SUBSCRIPTION_TYPE),
});

export const digitalDeliveryValidator = object().shape({
  included: array().of(digitalDeliveryBaseValidator),
  excluded: array().of(digitalDeliveryBaseValidator),
});

const MusicProductDigitalDeliveryAll = () => {
  const productId = useCustomParamId('id', '/music-product-digital');

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { getSelectOptions } = useEnumToSelectOptions();

  const {
    values,
    setFieldValue,
    validateField,
    errors,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  usePermissionGuard('DigitalDeliveryPermissionsLIST**');

  const minPublishDate =
    new Date(values.releaseDate).getTime() > new Date().getTime()
      ? formatDateToInput(values.releaseDate)
      : formatDateToInput(new Date());

  const digitalDeliveryCosistent =
    useGetMusicProductDigitalDeliveryConsistent(productId);

  const createAllDigitalDeliveries = usePostAllDigitalDeliveries();
  const updateAllDigitalDeliveries = usePutAllDigitalDeliveries();

  const { data: countries } = useGetCountries();

  useEffect(() => {
    setFieldValue('delivery', { priority: false, included: [], excluded: [] });
    if (digitalDeliveryCosistent.data && values.mainTitle.title) {
      console.log(
        middleware.digitalDelivery.queryToForm(digitalDeliveryCosistent.data)
      );
      setFieldValue(
        'delivery',
        middleware.digitalDelivery.queryToForm(digitalDeliveryCosistent.data)
      );
    }
  }, [digitalDeliveryCosistent.data, values.mainTitle.title]);

  const onSubmitDelivery = async () => {
    validateField('delivery');

    if (Object.keys(errors?.delivery ?? {}).length) {
      return toast.error(t('error-code.FORM_ERROR'));
    }

    if (values.delivery) {
      const deliveryIdentification = { productId };

      const delivery: ICreateAllDigitalDeliveries = {
        ...deliveryIdentification,
        included: values.delivery.included.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        excluded: values.delivery.excluded.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        priority: false,
      };

      return digitalDeliveryCosistent.data === undefined
        ? await createAllDigitalDeliveries.mutateAsync(delivery, {
            onSuccess: async () =>
              navigate(`/music-product-digital/${productId}/delivery`),
            onError: async error => onErrorShowAllErrorsToast(error),
          })
        : await updateAllDigitalDeliveries.mutateAsync(delivery, {
            onSuccess: async () =>
              navigate(`/music-product-digital/${productId}/delivery`),
            onError: async error => onErrorShowAllErrorsToast(error),
          });
    }
  };

  const getDeliveryNodes = (deliveries?: IDigitalDeliveryBase[]) => ({
    nodes: deliveries?.map((d, key) => ({ ...d, id: key })) ?? [],
  });

  const onAddDigitalDelivery = (prefix: 'included' | 'excluded') => {
    values.delivery &&
      setFieldValue(
        `delivery.${prefix}[${values.delivery[prefix].length}]`,
        digitalDeliveryInitialValues(minPublishDate)
      );
  };

  const onRemoveDigitalDelivery = (
    id: number,
    prefix: 'included' | 'excluded'
  ) => {
    values.delivery &&
      setFieldValue(
        `delivery.${prefix}`,
        values.delivery[prefix].filter((d, i) => i !== id)
      );
  };

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  const isDeliveryMethodManual =
    values.delivery?.deliveryType === DELIVERY_METHOD.Manual;

  const isDeliveryCanceled =
    values.delivery?.status === DELIVERY_STATUS.canceled;

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(`/music-product-digital/${productId}/delivery`)
              }>
              <Button.Icon icon={<PiCaretLeft />} />
            </Button>
          }
          title={
            digitalDeliveryCosistent.data === undefined
              ? t('button.publish-all')
              : t('button.change-all')
          }
          topbarContent={
            <div className='flex w-full items-center justify-end'>
              <div className='flex items-center gap-8 w-full justify-end font-medium'>
                <div className='flex gap-2'>
                  {!isDeliveryCanceled && (
                    <Popup.CancelWarning
                      transI18nKey={
                        digitalDeliveryCosistent.data === undefined
                          ? 'music-product-digital.delivery.publish-all-warning'
                          : 'music-product-digital.delivery.change-all-warning'
                      }
                      onCancel={onSubmitDelivery}
                      trigger={
                        <Button
                          variant={BUTTON_VARIANT.submit}
                          disabled={!values.delivery}>
                          <Button.Text
                            text={
                              digitalDeliveryCosistent.data === undefined
                                ? t('button.publish')
                                : t('button.change')
                            }
                          />
                          <Button.Icon icon={<PiGlobeSimple />} />
                        </Button>
                      }
                    />
                    // <Button
                    //   variant={BUTTON_VARIANT.submit}
                    //   onClick={onSubmitDelivery}
                    //   disabled={!values.delivery}>
                    //   <Button.Text
                    //     text={
                    //       digitalDeliveryCosistent.data === undefined
                    //         ? t('button.publish')
                    //         : t('button.change')
                    //     }
                    //   />
                    //   <Button.Icon icon={<PiGlobeSimple />} />
                    // </Button>
                  )}
                </div>
              </div>
            </div>
          }
        />
      }>
      {!isDeliveryMethodManual && (
        <div className='flex flex-col-reverse h-full w-full gap-4 justify-end '>
          <div className='flex w-full max-h-[45%] '>
            <Table
              id={'MusicProductDigitalDeliveryAll'}
              size='compact'
              data={getDeliveryNodes(values.delivery?.excluded)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.excluded-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.excluded[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].asap`}
                        label={t('input.label.asap')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={values.delivery?.excluded[id].asap}
                        />
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          disabled={values.delivery?.excluded[id].asap}
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'excluded')
                          }
                          isClear>
                          <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('excluded')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
          <div className='flex w-full max-h-[45%]'>
            <Table
              id={'MusicProductDigitalDeliveryAll'}
              size='compact'
              data={getDeliveryNodes(values.delivery?.included)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.included-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.included[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].asap`}
                        label={t('input.label.asap')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.included[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={values.delivery?.included[id].asap}
                        />
                        <FormInput.Text
                          name={`delivery.included[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          onChange={e => {
                            let value = e.target.value;
                            if (value && value.length === 5) {
                              value = `${value}:00`;
                            }
                            e.target.value = value;
                            e.persist();

                            const originalOnChange = e.target.onChange;
                            if (originalOnChange) originalOnChange(e);
                          }}
                          disabled={values.delivery?.included[id].asap}
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'included')
                          }
                          isClear>
                          <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('included')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
        </div>
      )}
    </TopbarPage>
  );
};

export default MusicProductDigitalDeliveryAll;
